import React, { useState, useEffect } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, KeyIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { apiService } from '../services/api';

interface APIKey {
  id: string;
  name: string;
  key_prefix: string;
  permissions: {
    read: boolean;
    write: boolean;
    admin: boolean;
    endpoints: string[];
  };
  rate_limit_per_minute: number;
  rate_limit_per_hour: number;
  rate_limit_per_day: number;
  is_active: boolean;
  expires_at?: string;
  last_used?: string;
  created_at: string;
  organization_id?: string;
}

interface CreateAPIKeyData {
  name: string;
  permissions: {
    read: boolean;
    write: boolean;
    admin: boolean;
    endpoints: string[];
  };
  rate_limit_per_minute: number;
  rate_limit_per_hour: number;
  rate_limit_per_day: number;
  organization_id?: string;
}

const APIKeysPage: React.FC = () => {
  const [apiKeys, setApiKeys] = useState<APIKey[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [selectedKey, setSelectedKey] = useState<APIKey | null>(null);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<string>('');
  const [showNewKey, setShowNewKey] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isRevoking, setIsRevoking] = useState<string | null>(null);
  const [showInactiveKeys, setShowInactiveKeys] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [keyToRevoke, setKeyToRevoke] = useState<APIKey | null>(null);

  const [formData, setFormData] = useState<CreateAPIKeyData>({
    name: '',
    permissions: {
      read: true,
      write: false,
      admin: false,
      endpoints: ['*']
    },
    rate_limit_per_minute: 100,
    rate_limit_per_hour: 1000,
    rate_limit_per_day: 10000
  });

  useEffect(() => {
    loadAPIKeys();
  }, []);

  const loadAPIKeys = async () => {
    try {
      setIsLoading(true);
      const response = await apiService.getAPIKeys();
      setApiKeys(response);
    } catch (error) {
      console.error('Failed to load API keys:', error);
      toast.error('Failed to load API keys');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateKey = async () => {
    try {
      setIsCreating(true);
      const response = await apiService.createAPIKey(formData);
      
      // Show the new key to the user
      setNewlyCreatedKey(response.key);
      setShowNewKey(true);
      
      // Reset form and close modal
      setFormData({
        name: '',
        permissions: {
          read: true,
          write: false,
          admin: false,
          endpoints: ['*']
        },
        rate_limit_per_minute: 100,
        rate_limit_per_hour: 1000,
        rate_limit_per_day: 10000
      });
      setShowCreateModal(false);
      
      // Reload keys
      await loadAPIKeys();
      
      toast.success('API key created successfully');
    } catch (error) {
      console.error('Failed to create API key:', error);
      toast.error('Failed to create API key');
    } finally {
      setIsCreating(false);
    }
  };

  const handleRegenerateKey = async () => {
    if (!selectedKey) return;
    
    try {
      setIsRegenerating(true);
      const response = await apiService.regenerateAPIKey(selectedKey.id);
      
      // Show the new key to the user
      setNewlyCreatedKey(response.key);
      setShowNewKey(true);
      
      // Close modal
      setShowRegenerateModal(false);
      setSelectedKey(null);
      
      // Reload keys
      await loadAPIKeys();
      
      toast.success('API key regenerated successfully');
    } catch (error) {
      console.error('Failed to regenerate API key:', error);
      toast.error('Failed to regenerate API key');
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleRevokeKey = (keyId: string) => {
    const key = apiKeys.find(k => k.id === keyId);
    if (key) {
      setKeyToRevoke(key);
      setShowRevokeModal(true);
    }
  };

  const confirmRevokeKey = async () => {
    if (!keyToRevoke) return;
    
    try {
      setIsRevoking(keyToRevoke.id);
      await apiService.revokeAPIKey(keyToRevoke.id);
      
      // Reload keys
      await loadAPIKeys();
      
      toast.success('API key revoked successfully');
      setShowRevokeModal(false);
      setKeyToRevoke(null);
    } catch (error) {
      console.error('Failed to revoke API key:', error);
      toast.error('Failed to revoke API key');
    } finally {
      setIsRevoking(null);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatPermissions = (permissions: any) => {
    const perms = [];
    if (permissions.read) perms.push('Read');
    if (permissions.write) perms.push('Write');
    if (permissions.admin) perms.push('Admin');
    return perms.join(', ') || 'None';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">API Keys</h1>
              <p className="mt-1 sm:mt-2 text-sm sm:text-base text-gray-600">
                Manage your API keys for accessing TrendBeat data and services
              </p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 w-full sm:w-auto"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Create API Key
            </button>
          </div>
        </div>

        {/* Filter Toggle */}
        {apiKeys.length > 0 && (
          <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center">
              <span className="text-sm font-medium text-gray-700">
                Showing {showInactiveKeys ? apiKeys.length : apiKeys.filter(key => key.is_active).length} of {apiKeys.length} keys
              </span>
            </div>
            <div className="flex items-center justify-between sm:justify-end space-x-3">
              <span className="text-sm text-gray-600">Show inactive keys</span>
              <button
                onClick={() => setShowInactiveKeys(!showInactiveKeys)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  showInactiveKeys ? 'bg-purple-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    showInactiveKeys ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        )}

        {/* API Keys List */}
        <div className="bg-white shadow rounded-lg">
          {apiKeys.filter(key => showInactiveKeys || key.is_active).length === 0 ? (
            <div className="text-center py-8 sm:py-12 px-4">
              <KeyIcon className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-gray-400" />
              <h3 className="mt-2 text-base sm:text-lg font-medium text-gray-900">
                {apiKeys.length === 0 ? 'No API keys' : 'No active API keys'}
              </h3>
              <p className="mt-1 text-sm text-gray-500 max-w-sm mx-auto">
                {apiKeys.length === 0 
                  ? 'Get started by creating your first API key.'
                  : 'All your API keys are currently inactive. Toggle "Show inactive keys" above to see them.'
                }
              </p>
              <div className="mt-4 sm:mt-6">
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 w-full sm:w-auto"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Create API Key
                </button>
              </div>
            </div>
          ) : (
            <div className="p-4 sm:p-6">
              {/* Grid of API Key Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
                {apiKeys
                  .filter(key => showInactiveKeys || key.is_active)
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((key) => (
                  <div key={key.id} className={`bg-gradient-to-br border rounded-xl p-3 sm:p-4 hover:shadow-lg transition-all duration-300 group min-h-[280px] sm:min-h-[320px] flex flex-col ${
                    key.is_active 
                      ? 'from-white to-gray-50 border-gray-200 hover:border-gray-300' 
                      : 'from-gray-50 to-gray-100 border-gray-300 opacity-75 hover:border-gray-400'
                  }`}>
                    {/* Header */}
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <div className="flex-shrink-0 w-2 h-2 bg-gradient-to-r from-purple-400 to-purple-600 rounded-full"></div>
                          <h3 className="text-base font-bold text-gray-900 truncate group-hover:text-purple-700 transition-colors">{key.name}</h3>
                        </div>
                        {key.organization_id && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 border border-blue-200">
                            Team Key
                          </span>
                        )}
                      </div>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold shadow-sm ${
                        key.is_active 
                          ? 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border border-green-200' 
                          : 'bg-gradient-to-r from-red-100 to-rose-100 text-red-800 border border-red-200'
                      }`}>
                        {key.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>

                    {/* Key Prefix */}
                    <div className="mb-3">
                      <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Key Prefix</label>
                      <div className="mt-1">
                        <code className="text-sm bg-gradient-to-r from-gray-100 to-gray-200 px-3 py-1.5 rounded-lg font-mono text-gray-800 border border-gray-300 shadow-sm">
                          {key.key_prefix}
                        </code>
                      </div>
                    </div>

                    {/* Permissions */}
                    <div className="mb-3">
                      <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Permissions</label>
                      <div className="mt-1 flex flex-wrap gap-1.5 min-h-[2rem]">
                        {formatPermissions(key.permissions).split(', ').map((perm, index) => (
                          <span key={index} className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold bg-gradient-to-r from-purple-100 to-violet-100 text-purple-800 border border-purple-200 shadow-sm">
                            {perm}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Rate Limits */}
                    <div className="mb-3">
                      <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Rate Limits</label>
                      <div className="mt-1 grid grid-cols-3 gap-1 sm:gap-2">
                        <div className="text-center bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-2 border border-gray-200">
                          <div className="text-sm font-bold text-gray-900">{key.rate_limit_per_minute}</div>
                          <div className="text-xs text-gray-600 font-medium">per min</div>
                        </div>
                        <div className="text-center bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-2 border border-gray-200">
                          <div className="text-sm font-bold text-gray-900">{key.rate_limit_per_hour}</div>
                          <div className="text-xs text-gray-600 font-medium">per hour</div>
                        </div>
                        <div className="text-center bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-2 border border-gray-200">
                          <div className="text-sm font-bold text-gray-900">{key.rate_limit_per_day}</div>
                          <div className="text-xs text-gray-600 font-medium">per day</div>
                        </div>
                      </div>
                    </div>

                    {/* Last Used */}
                    <div className="mb-4">
                      <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Last Used</label>
                      <div className="mt-1 text-sm font-medium text-gray-700">
                        {key.last_used ? formatDate(key.last_used) : 'Never'}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2 pt-3 border-t border-gray-200/70 mt-auto">
                      <button
                        onClick={() => {
                          setSelectedKey(key);
                          setShowRegenerateModal(true);
                        }}
                        className="w-full inline-flex items-center justify-center px-2 py-1.5 border border-purple-300 text-xs font-medium rounded-md text-purple-700 bg-gradient-to-r from-white to-purple-50 hover:from-purple-50 hover:to-purple-100 hover:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                        disabled={!key.is_active}
                      >
                        <svg className="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Regenerate
                      </button>
                      <button
                        onClick={() => handleRevokeKey(key.id)}
                        disabled={isRevoking === key.id || !key.is_active}
                        className="w-full inline-flex items-center justify-center px-2 py-1.5 border border-red-300 text-xs font-medium rounded-md text-red-700 bg-gradient-to-r from-white to-red-50 hover:from-red-50 hover:to-red-100 hover:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                      >
                        {isRevoking === key.id ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-1 h-3 w-3" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Revoking...
                          </>
                        ) : (
                          <>
                            <svg className="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            {key.is_active ? 'Revoke' : 'Revoked'}
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Create API Key Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Create New API Key</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Name</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                      placeholder="My API Key"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Permissions</label>
                    <div className="mt-2 space-y-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.permissions.read}
                          onChange={(e) => setFormData({
                            ...formData, 
                            permissions: {...formData.permissions, read: e.target.checked}
                          })}
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">Read</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.permissions.write}
                          onChange={(e) => setFormData({
                            ...formData, 
                            permissions: {...formData.permissions, write: e.target.checked}
                          })}
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">Write</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.permissions.admin}
                          onChange={(e) => setFormData({
                            ...formData, 
                            permissions: {...formData.permissions, admin: e.target.checked}
                          })}
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">Admin</span>
                      </label>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Rate Limits</label>
                    <div className="mt-2 grid grid-cols-3 gap-2">
                      <div>
                        <label className="block text-xs text-gray-500">Per Minute</label>
                        <input
                          type="number"
                          value={formData.rate_limit_per_minute}
                          onChange={(e) => setFormData({...formData, rate_limit_per_minute: parseInt(e.target.value)})}
                          className="block w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
                          min="1"
                          max="10000"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500">Per Hour</label>
                        <input
                          type="number"
                          value={formData.rate_limit_per_hour}
                          onChange={(e) => setFormData({...formData, rate_limit_per_hour: parseInt(e.target.value)})}
                          className="block w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
                          min="1"
                          max="100000"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500">Per Day</label>
                        <input
                          type="number"
                          value={formData.rate_limit_per_day}
                          onChange={(e) => setFormData({...formData, rate_limit_per_day: parseInt(e.target.value)})}
                          className="block w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
                          min="1"
                          max="1000000"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateKey}
                    disabled={!formData.name || isCreating}
                    className="px-4 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-md hover:bg-purple-700 disabled:opacity-50"
                  >
                    {isCreating ? 'Creating...' : 'Create Key'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Regenerate API Key Modal */}
        {showRegenerateModal && selectedKey && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Regenerate API Key</h3>
                <p className="text-sm text-gray-600 mb-4">
                  This will create a new API key and invalidate the old one. The old key will no longer work.
                </p>
                
                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowRegenerateModal(false);
                      setSelectedKey(null);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleRegenerateKey}
                    disabled={isRegenerating}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50"
                  >
                    {isRegenerating ? 'Regenerating...' : 'Regenerate Key'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Revoke API Key Confirmation Modal */}
        {showRevokeModal && keyToRevoke && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0">
                    <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-lg font-medium text-gray-900">Revoke API Key</h3>
                  </div>
                </div>
                
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">
                    Are you sure you want to revoke the API key <strong>"{keyToRevoke.name}"</strong>?
                  </p>
                  <p className="text-sm text-red-600 font-medium">
                    This action cannot be undone. The key will immediately stop working.
                  </p>
                </div>
                
                <div className="bg-gray-50 p-3 rounded-md mb-4">
                  <p className="text-xs text-gray-600 font-medium">Key Prefix</p>
                  <code className="text-sm text-gray-800">{keyToRevoke.key_prefix}</code>
                </div>
                
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowRevokeModal(false);
                      setKeyToRevoke(null);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
                    disabled={isRevoking === keyToRevoke?.id}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmRevokeKey}
                    disabled={isRevoking === keyToRevoke?.id}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isRevoking === keyToRevoke?.id ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Revoking...
                      </span>
                    ) : (
                      'Revoke Key'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Show New/Regenerated Key Modal */}
        {showNewKey && newlyCreatedKey && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Your New API Key</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Copy this key now! It won't be shown again.
                </p>
                
                <div className="bg-gray-100 p-3 rounded-md">
                  <code className="text-sm break-all">{newlyCreatedKey}</code>
                </div>
                
                <div className="mt-4 flex justify-end space-x-3">
                  <button
                    onClick={() => copyToClipboard(newlyCreatedKey)}
                    className="px-4 py-2 text-sm font-medium text-purple-600 bg-white border border-purple-300 rounded-md hover:bg-purple-50"
                  >
                    Copy to Clipboard
                  </button>
                  <button
                    onClick={() => {
                      setShowNewKey(false);
                      setNewlyCreatedKey('');
                    }}
                    className="px-4 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-md hover:bg-purple-700"
                  >
                    Done
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default APIKeysPage;